import { createContext, useReducer, useEffect } from 'react';
import { customers as defaultCustomers, dispensingQueue as defaultQueue, medications, locations, stockTransfers as defaultTransfers } from '../data';

export const AppContext = createContext();

// Bump this version when data structure changes to force localStorage reset
const DATA_VERSION = 5;

const loadState = () => {
  try {
    const saved = localStorage.getItem('medfast-pharmacy-state');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Invalidate stale localStorage when data version changes
      if (parsed._dataVersion !== DATA_VERSION) {
        localStorage.removeItem('medfast-pharmacy-state');
        return null;
      }
      return parsed;
    }
  } catch (e) {
    // Ignore parse errors
  }
  return null;
};

const savedState = loadState();

// Build inventory from medications data with stock levels and MRP data
const buildInventory = () =>
  medications.map(med => ({
    ...med,
    stockLevel: med.stockLevel,
    stockByLocation: med.stockByLocation || {},
    reorderThreshold: med.mrp?.reorderPoint || 10,
    lastRestocked: med.mrp?.lastReceived || '2026-02-10'
  }));

// Merge saved orders with default orders — ensures new facility orders appear even with stale localStorage
const mergeOrders = (saved, defaults) => {
  if (!saved) return defaults;
  const savedIds = new Set(saved.map(o => o.id));
  const missing = defaults.filter(o => !savedIds.has(o.id));
  return [...saved, ...missing];
};

export const initialState = {
  staff: {
    name: 'Alex Pharmacist',
    role: 'Lead Pharmacist',
    ahpraNumber: 'PHA0001234567',
    pharmacyName: 'Blooms The Chemist Melbourne Central',
    pharmacyId: 'BLOOMS001'
  },
  activeLocationId: savedState?.activeLocationId || 'LOC001',
  locations,
  stockTransfers: savedState?.stockTransfers || defaultTransfers,
  customers: defaultCustomers,
  selectedCustomerId: null,
  customerSubTab: 'profile',
  activeTab: 'dashboard',
  searchQuery: '',
  dispensingQueue: savedState?.dispensingQueue || defaultQueue,
  selectedDispensingId: null,
  emergencySupply: null,
  orders: mergeOrders(savedState?.orders, [
    {
      id: 'ORD001',
      locationId: 'LOC001',
      customerId: 'CUST001',
      customerName: 'Sarah Johnson',
      items: [{ name: 'Metformin 500mg', quantity: 60, price: 6.80 }, { name: 'Sertraline 50mg', quantity: 30, price: 6.80 }],
      total: 13.60,
      status: 'Filling',
      pharmacy: 'Blooms The Chemist Melbourne Central',
      deliveryMethod: 'Self Pickup',
      createdAt: '2026-02-15T08:00:00',
      updatedAt: '2026-02-15T09:30:00'
    },
    {
      id: 'ORD002',
      locationId: 'LOC001',
      customerId: 'CUST008',
      customerName: "Michael O'Brien",
      items: [{ name: 'Amlodipine 5mg', quantity: 30, price: 6.80 }, { name: 'Donepezil 10mg', quantity: 30, price: 6.80 }],
      total: 13.60,
      status: 'Verifying',
      pharmacy: 'Blooms The Chemist Melbourne Central',
      deliveryMethod: 'Delivery',
      createdAt: '2026-02-15T07:30:00',
      updatedAt: '2026-02-15T08:00:00',
      notes: 'Blister pack — deliver to Sunshine Aged Care'
    },
    {
      id: 'ORD003',
      locationId: 'LOC001',
      customerId: 'CUST004',
      customerName: 'Robert Thompson',
      items: [{ name: 'Atorvastatin 20mg', quantity: 30, price: 6.80 }],
      total: 6.80,
      status: 'Received',
      pharmacy: 'Blooms The Chemist Melbourne Central',
      deliveryMethod: 'Self Pickup',
      createdAt: '2026-02-15T09:00:00',
      updatedAt: '2026-02-15T09:00:00'
    },
    // Facility Orders — Aged Care
    {
      id: 'FORD001',
      locationId: 'LOC001',
      type: 'facility',
      facilityType: 'Aged Care',
      facilityName: 'Sunshine Aged Care',
      facilityContact: 'Margaret Chen — Nurse Manager',
      facilityPhone: '+61 3 9555 1234',
      facilityAddress: '45 Garden Rd, Toorak VIC 3142',
      status: 'Preparing',
      deliveryMethod: 'Pharmacy Delivery',
      deliverySchedule: 'Weekly — Monday AM',
      pharmacy: 'Blooms The Chemist Melbourne Central',
      createdAt: '2025-07-14T14:00:00',
      updatedAt: '2025-07-15T08:00:00',
      notes: 'Weekly Webster-pak delivery. All blister packs. Deliver to nursing station — Building A.',
      patients: [
        {
          name: "Michael O'Brien",
          customerId: 'CUST008',
          room: 'Room 12',
          packType: 'Webster-pak',
          items: [
            { name: 'Amlodipine 5mg', quantity: 7, price: 6.80 },
            { name: 'Donepezil 10mg', quantity: 7, price: 6.80 },
            { name: 'Vitamin D 1000IU', quantity: 7, price: 0 },
            { name: 'Pantoprazole 40mg', quantity: 7, price: 6.80 }
          ]
        },
        {
          name: 'Dorothy Evans',
          room: 'Room 8',
          packType: 'Webster-pak',
          items: [
            { name: 'Metoprolol 50mg', quantity: 14, price: 6.80 },
            { name: 'Warfarin 5mg', quantity: 7, price: 6.80 },
            { name: 'Frusemide 40mg', quantity: 7, price: 6.80 },
            { name: 'Omeprazole 20mg', quantity: 7, price: 6.80 }
          ]
        },
        {
          name: 'Harold Williams',
          room: 'Room 15',
          packType: 'Webster-pak',
          items: [
            { name: 'Metformin 500mg', quantity: 14, price: 6.80 },
            { name: 'Amlodipine 5mg', quantity: 7, price: 6.80 },
            { name: 'Rosuvastatin 10mg', quantity: 7, price: 6.80 }
          ]
        },
        {
          name: 'Edna McCarthy',
          room: 'Room 3',
          packType: 'Webster-pak',
          items: [
            { name: 'Sertraline 50mg', quantity: 7, price: 6.80 },
            { name: 'Paracetamol 500mg', quantity: 14, price: 0 },
            { name: 'Calcium + Vitamin D', quantity: 7, price: 6.80 }
          ]
        }
      ],
      total: 108.80
    },
    {
      id: 'FORD002',
      locationId: 'LOC001',
      type: 'facility',
      facilityType: 'Aged Care',
      facilityName: 'Toorak Gardens Residential Care',
      facilityContact: 'Susan Park — Director of Nursing',
      facilityPhone: '+61 3 9866 5500',
      facilityAddress: '120 Toorak Rd, Toorak VIC 3142',
      status: 'Processing',
      deliveryMethod: 'Pharmacy Delivery',
      deliverySchedule: 'Fortnightly — Wednesday AM',
      pharmacy: 'Blooms The Chemist Melbourne Central',
      createdAt: '2025-07-15T07:00:00',
      updatedAt: '2025-07-15T07:00:00',
      notes: 'Fortnightly blister pack delivery. 2 new residents added this cycle. PRN medications packed separately.',
      patients: [
        {
          name: 'Frank Morrison',
          room: 'Unit 4A',
          packType: 'Webster-pak',
          items: [
            { name: 'Lisinopril 10mg', quantity: 14, price: 6.80 },
            { name: 'Atorvastatin 20mg', quantity: 14, price: 6.80 },
            { name: 'Aspirin 100mg', quantity: 14, price: 0 },
            { name: 'Metformin 500mg', quantity: 28, price: 6.80 }
          ]
        },
        {
          name: 'Jean Campbell',
          room: 'Unit 7B',
          packType: 'Webster-pak',
          isNew: true,
          items: [
            { name: 'Amlodipine 5mg', quantity: 14, price: 6.80 },
            { name: 'Escitalopram 10mg', quantity: 14, price: 6.80 },
            { name: 'Temazepam 10mg', quantity: 14, price: 6.80 }
          ]
        },
        {
          name: 'Arthur Nguyen',
          room: 'Unit 2A',
          packType: 'Webster-pak',
          items: [
            { name: 'Metoprolol 50mg', quantity: 28, price: 6.80 },
            { name: 'Rosuvastatin 10mg', quantity: 14, price: 6.80 },
            { name: 'Pantoprazole 40mg', quantity: 14, price: 6.80 },
            { name: 'Tamsulosin 0.4mg', quantity: 14, price: 6.80 },
            { name: 'Donepezil 5mg', quantity: 14, price: 6.80 }
          ]
        }
      ],
      total: 115.60
    },
    // NDIS Facility Orders
    {
      id: 'FORD003',
      locationId: 'LOC001',
      type: 'facility',
      facilityType: 'NDIS',
      facilityName: 'Access Community Support Services',
      facilityContact: 'Rachel Adams — Support Coordinator',
      facilityPhone: '+61 3 9111 2233',
      facilityAddress: '88 Chapel St, Windsor VIC 3181',
      ndisProviderNumber: '405 123 456 78',
      status: 'Processing',
      deliveryMethod: 'Pharmacy Delivery',
      deliverySchedule: 'Monthly',
      pharmacy: 'Blooms The Chemist Melbourne Central',
      createdAt: '2025-07-15T09:30:00',
      updatedAt: '2025-07-15T09:30:00',
      notes: 'NDIS Plan Managed participants. Medications and continence aids. Invoices to Plan Partners Australia. Deliver to group home at 22 Chapel St.',
      participants: [
        {
          name: 'Tom Wilson',
          ndisNumber: '431 234 567',
          planManager: 'Plan Partners Australia',
          supportCategory: 'Core — Consumables',
          items: [
            { name: 'Risperidone 1mg', quantity: 30, price: 6.80, type: 'medication' },
            { name: 'Sodium Valproate 200mg', quantity: 60, price: 6.80, type: 'medication' },
            { name: 'Continence Pads (Pack 20)', quantity: 4, price: 28.50, type: 'consumable' }
          ]
        },
        {
          name: 'Amy Richards',
          ndisNumber: '431 567 890',
          planManager: 'Plan Partners Australia',
          supportCategory: 'Core — Consumables',
          items: [
            { name: 'Escitalopram 10mg', quantity: 30, price: 6.80, type: 'medication' },
            { name: 'Melatonin 2mg', quantity: 30, price: 6.80, type: 'medication' },
            { name: 'Thickening Powder (900g)', quantity: 2, price: 24.00, type: 'consumable' },
            { name: 'Latex-Free Gloves Box 100', quantity: 2, price: 15.50, type: 'consumable' }
          ]
        },
        {
          name: 'Jake Patterson',
          ndisNumber: '431 890 123',
          planManager: 'My Plan Manager',
          supportCategory: 'Core — Consumables',
          items: [
            { name: 'Methylphenidate 10mg', quantity: 30, price: 42.49, type: 'medication' },
            { name: 'Melatonin 2mg', quantity: 30, price: 6.80, type: 'medication' }
          ]
        }
      ],
      total: 252.49
    },
    {
      id: 'FORD004',
      locationId: 'LOC001',
      type: 'facility',
      facilityType: 'NDIS',
      facilityName: 'Bright Horizons Disability Services',
      facilityContact: 'Daniel Cooper — Team Leader',
      facilityPhone: '+61 3 9444 5566',
      facilityAddress: '15 Station Rd, Box Hill VIC 3128',
      ndisProviderNumber: '405 987 654 32',
      status: 'Ready for Dispatch',
      deliveryMethod: 'Pharmacy Delivery',
      deliverySchedule: 'Fortnightly',
      pharmacy: 'Blooms The Chemist Melbourne Central',
      createdAt: '2025-07-13T10:00:00',
      updatedAt: '2025-07-15T09:00:00',
      notes: 'Fortnightly delivery. Supported Independent Living (SIL) house. Leave medications with on-site support worker. All Webster-pak.',
      participants: [
        {
          name: 'Sophie Chen',
          ndisNumber: '431 456 789',
          planManager: 'NDIS Self-Managed',
          supportCategory: 'Core — Consumables',
          items: [
            { name: 'Sodium Valproate 500mg', quantity: 28, price: 6.80, type: 'medication' },
            { name: 'Quetiapine 25mg', quantity: 14, price: 6.80, type: 'medication' }
          ]
        },
        {
          name: 'Marcus Brown',
          ndisNumber: '431 321 654',
          planManager: 'National Plan Managers',
          supportCategory: 'Core — Consumables',
          items: [
            { name: 'Sertraline 100mg', quantity: 14, price: 6.80, type: 'medication' },
            { name: 'Paracetamol 500mg', quantity: 28, price: 0, type: 'medication' },
            { name: 'Wound Care Kit', quantity: 1, price: 18.90, type: 'consumable' }
          ]
        }
      ],
      total: 39.30
    },
    // Large Aged Care — Dementia Wing
    {
      id: 'FORD005',
      locationId: 'LOC001',
      type: 'facility',
      facilityType: 'Aged Care',
      facilityName: 'St Vincent\'s Aged Care — Dementia Wing',
      facilityContact: 'Karen Fletcher — Clinical Nurse Consultant',
      facilityPhone: '+61 3 9333 7700',
      facilityAddress: '200 Burke Rd, Camberwell VIC 3124',
      status: 'Processing',
      deliveryMethod: 'Pharmacy Delivery',
      deliverySchedule: 'Weekly — Thursday AM',
      pharmacy: 'Blooms The Chemist Melbourne Central',
      createdAt: '2025-07-15T06:30:00',
      updatedAt: '2025-07-15T06:30:00',
      notes: 'Dementia wing — all residents require crushing charts attached to packs. PRN Lorazepam packed separately with S4 log sheets. Contact Karen for any changes.',
      patients: [
        {
          name: 'Nora Fitzpatrick',
          room: 'DW-01',
          packType: 'Webster-pak (Crushed)',
          items: [
            { name: 'Donepezil 10mg', quantity: 7, price: 6.80 },
            { name: 'Memantine 10mg', quantity: 7, price: 6.80 },
            { name: 'Sertraline 50mg', quantity: 7, price: 6.80 },
            { name: 'Pantoprazole 40mg', quantity: 7, price: 6.80 },
            { name: 'Vitamin D 1000IU', quantity: 7, price: 0 }
          ]
        },
        {
          name: 'George Papadopoulos',
          room: 'DW-02',
          packType: 'Webster-pak (Crushed)',
          items: [
            { name: 'Risperidone 0.5mg', quantity: 14, price: 6.80 },
            { name: 'Amlodipine 5mg', quantity: 7, price: 6.80 },
            { name: 'Metformin 500mg', quantity: 14, price: 6.80 },
            { name: 'Aspirin 100mg', quantity: 7, price: 0 }
          ]
        },
        {
          name: 'Margaret Hawkins',
          room: 'DW-04',
          packType: 'Webster-pak (Crushed)',
          isNew: true,
          items: [
            { name: 'Donepezil 5mg', quantity: 7, price: 6.80 },
            { name: 'Escitalopram 10mg', quantity: 7, price: 6.80 },
            { name: 'Frusemide 20mg', quantity: 7, price: 6.80 },
            { name: 'Paracetamol 500mg', quantity: 14, price: 0 },
            { name: 'Calcium + Vitamin D', quantity: 7, price: 6.80 },
            { name: 'Coloxyl + Senna', quantity: 7, price: 0 }
          ]
        },
        {
          name: 'Stanley Wong',
          room: 'DW-05',
          packType: 'Webster-pak (Crushed)',
          items: [
            { name: 'Quetiapine 25mg', quantity: 14, price: 6.80 },
            { name: 'Levodopa/Carbidopa 100/25mg', quantity: 21, price: 6.80 },
            { name: 'Omeprazole 20mg', quantity: 7, price: 6.80 },
            { name: 'Bisoprolol 2.5mg', quantity: 7, price: 6.80 }
          ]
        },
        {
          name: 'Irene Castellano',
          room: 'DW-06',
          packType: 'Webster-pak (Crushed)',
          items: [
            { name: 'Galantamine 8mg', quantity: 7, price: 6.80 },
            { name: 'Mirtazapine 15mg', quantity: 7, price: 6.80 },
            { name: 'Perindopril 5mg', quantity: 7, price: 6.80 },
            { name: 'Rosuvastatin 10mg', quantity: 7, price: 6.80 }
          ]
        },
        {
          name: 'Alberto Russo',
          room: 'DW-07',
          packType: 'Webster-pak (Crushed)',
          items: [
            { name: 'Olanzapine 5mg', quantity: 7, price: 6.80 },
            { name: 'Sodium Valproate 200mg', quantity: 14, price: 6.80 },
            { name: 'Atorvastatin 40mg', quantity: 7, price: 6.80 },
            { name: 'Paracetamol 500mg', quantity: 14, price: 0 },
            { name: 'Vitamin B12 1000mcg', quantity: 7, price: 0 }
          ]
        }
      ],
      total: 176.80
    },
    // Large Aged Care — High Care / Palliative
    {
      id: 'FORD006',
      locationId: 'LOC001',
      type: 'facility',
      facilityType: 'Aged Care',
      facilityName: 'Bayside Manor Nursing Home',
      facilityContact: 'Dr. Helen Tran — Facility GP / Lisa Murray — DON',
      facilityPhone: '+61 3 9599 8800',
      facilityAddress: '55 Beach Rd, Brighton VIC 3186',
      status: 'Packing',
      deliveryMethod: 'Pharmacy Delivery',
      deliverySchedule: 'Weekly — Tuesday PM',
      pharmacy: 'Blooms The Chemist Melbourne Central',
      createdAt: '2025-07-14T11:00:00',
      updatedAt: '2025-07-15T10:30:00',
      notes: 'High care wing. Palliative residents require S8 register copies. Insulin pens delivered in cold chain bag. Contact Lisa for delivery — buzzer code 4421#.',
      patients: [
        {
          name: 'Vera Hutchinson',
          room: 'HC-01',
          packType: 'Webster-pak',
          items: [
            { name: 'Morphine SR 10mg', quantity: 14, price: 6.80 },
            { name: 'Metoclopramide 10mg', quantity: 21, price: 6.80 },
            { name: 'Dexamethasone 4mg', quantity: 7, price: 6.80 },
            { name: 'Pantoprazole 40mg', quantity: 7, price: 6.80 },
            { name: 'Docusate + Senna', quantity: 7, price: 0 }
          ]
        },
        {
          name: 'Patrick Gallagher',
          room: 'HC-03',
          packType: 'Webster-pak',
          items: [
            { name: 'Insulin Glargine Pen', quantity: 1, price: 6.80 },
            { name: 'Metformin 1000mg', quantity: 14, price: 6.80 },
            { name: 'Perindopril 5mg', quantity: 7, price: 6.80 },
            { name: 'Atorvastatin 40mg', quantity: 7, price: 6.80 },
            { name: 'Aspirin 100mg', quantity: 7, price: 0 },
            { name: 'Lantus Needles 31G Box', quantity: 1, price: 6.80 }
          ]
        },
        {
          name: 'Betty Tran',
          room: 'HC-05',
          packType: 'Webster-pak',
          isNew: true,
          items: [
            { name: 'Fentanyl Patch 25mcg', quantity: 2, price: 6.80 },
            { name: 'Ondansetron 4mg', quantity: 14, price: 6.80 },
            { name: 'Temazepam 10mg', quantity: 7, price: 6.80 },
            { name: 'Paracetamol 500mg', quantity: 28, price: 0 }
          ]
        },
        {
          name: 'Ronald Simmons',
          room: 'HC-06',
          packType: 'Webster-pak',
          items: [
            { name: 'Oxycodone SR 10mg', quantity: 14, price: 6.80 },
            { name: 'Pregabalin 75mg', quantity: 14, price: 6.80 },
            { name: 'Mirtazapine 30mg', quantity: 7, price: 6.80 },
            { name: 'Pantoprazole 40mg', quantity: 7, price: 6.80 },
            { name: 'Senna 8.6mg', quantity: 7, price: 0 }
          ]
        },
        {
          name: 'Joan Petersen',
          room: 'HC-08',
          packType: 'Webster-pak',
          items: [
            { name: 'Warfarin 3mg', quantity: 7, price: 6.80 },
            { name: 'Digoxin 62.5mcg', quantity: 7, price: 6.80 },
            { name: 'Frusemide 40mg', quantity: 7, price: 6.80 },
            { name: 'Spironolactone 25mg', quantity: 7, price: 6.80 },
            { name: 'Potassium Chloride 600mg', quantity: 7, price: 6.80 }
          ]
        },
        {
          name: 'William Sato',
          room: 'HC-10',
          packType: 'Webster-pak',
          items: [
            { name: 'Levodopa/Carbidopa 250/25mg', quantity: 28, price: 6.80 },
            { name: 'Amantadine 100mg', quantity: 14, price: 6.80 },
            { name: 'Quetiapine 25mg', quantity: 7, price: 6.80 },
            { name: 'Omeprazole 20mg', quantity: 7, price: 6.80 }
          ]
        },
        {
          name: 'Agnes Kowalski',
          room: 'HC-11',
          packType: 'Webster-pak',
          items: [
            { name: 'Rivaroxaban 20mg', quantity: 7, price: 6.80 },
            { name: 'Bisoprolol 5mg', quantity: 7, price: 6.80 },
            { name: 'Candesartan 16mg', quantity: 7, price: 6.80 },
            { name: 'Rosuvastatin 20mg', quantity: 7, price: 6.80 },
            { name: 'Paracetamol 500mg', quantity: 14, price: 0 }
          ]
        }
      ],
      total: 258.40
    },
    // Large NDIS — Group Home
    {
      id: 'FORD007',
      locationId: 'LOC001',
      type: 'facility',
      facilityType: 'NDIS',
      facilityName: 'Yarra Ranges Community Living',
      facilityContact: 'Sarah Brennan — House Coordinator',
      facilityPhone: '+61 3 9751 3300',
      facilityAddress: '12 Mountain View Cr, Lilydale VIC 3140',
      ndisProviderNumber: '405 222 333 44',
      status: 'Processing',
      deliveryMethod: 'Pharmacy Delivery',
      deliverySchedule: 'Fortnightly — Monday',
      pharmacy: 'Blooms The Chemist Melbourne Central',
      createdAt: '2025-07-15T08:00:00',
      updatedAt: '2025-07-15T08:00:00',
      notes: 'Group home — 5 residents with intellectual disability. All Webster-pak. Sarah or duty support worker must sign for delivery. Controlled meds require double-sign.',
      participants: [
        {
          name: 'Liam Fraser',
          ndisNumber: '431 111 222',
          planManager: 'Maple Plan Management',
          supportCategory: 'Core — Consumables',
          items: [
            { name: 'Sodium Valproate 500mg', quantity: 28, price: 6.80, type: 'medication' },
            { name: 'Risperidone 1mg', quantity: 28, price: 6.80, type: 'medication' },
            { name: 'Omeprazole 20mg', quantity: 14, price: 6.80, type: 'medication' },
            { name: 'Continence Pads (Pack 20)', quantity: 6, price: 28.50, type: 'consumable' }
          ]
        },
        {
          name: 'Hannah Douglas',
          ndisNumber: '431 222 333',
          planManager: 'Maple Plan Management',
          supportCategory: 'Core — Consumables',
          items: [
            { name: 'Lamotrigine 100mg', quantity: 28, price: 6.80, type: 'medication' },
            { name: 'Escitalopram 20mg', quantity: 14, price: 6.80, type: 'medication' },
            { name: 'Thickening Powder (900g)', quantity: 3, price: 24.00, type: 'consumable' },
            { name: 'Latex-Free Gloves Box 100', quantity: 3, price: 15.50, type: 'consumable' }
          ]
        },
        {
          name: 'Ethan Maher',
          ndisNumber: '431 333 444',
          planManager: 'Maple Plan Management',
          supportCategory: 'Core — Consumables',
          isNew: true,
          items: [
            { name: 'Aripiprazole 10mg', quantity: 14, price: 6.80, type: 'medication' },
            { name: 'Clonidine 100mcg', quantity: 28, price: 6.80, type: 'medication' },
            { name: 'Melatonin 2mg', quantity: 14, price: 6.80, type: 'medication' },
            { name: 'Continence Pads (Pack 20)', quantity: 4, price: 28.50, type: 'consumable' }
          ]
        },
        {
          name: 'Chloe Barton',
          ndisNumber: '431 444 555',
          planManager: 'My Plan Manager',
          supportCategory: 'Core — Consumables',
          items: [
            { name: 'Topiramate 50mg', quantity: 28, price: 6.80, type: 'medication' },
            { name: 'Levetiracetam 500mg', quantity: 28, price: 6.80, type: 'medication' },
            { name: 'Folic Acid 5mg', quantity: 14, price: 0, type: 'medication' }
          ]
        },
        {
          name: 'Noah Sinclair',
          ndisNumber: '431 555 666',
          planManager: 'Maple Plan Management',
          supportCategory: 'Core — Consumables',
          items: [
            { name: 'Olanzapine 10mg', quantity: 14, price: 6.80, type: 'medication' },
            { name: 'Metformin 500mg', quantity: 28, price: 6.80, type: 'medication' },
            { name: 'Paracetamol 500mg', quantity: 28, price: 0, type: 'medication' },
            { name: 'Wound Care Kit', quantity: 2, price: 18.90, type: 'consumable' }
          ]
        }
      ],
      total: 476.90
    },
    // NDIS — Day Program + Multiple Sites
    {
      id: 'FORD008',
      locationId: 'LOC001',
      type: 'facility',
      facilityType: 'NDIS',
      facilityName: 'Whittlesea Supported Living Network',
      facilityContact: 'James Khalil — Regional Manager',
      facilityPhone: '+61 3 9401 2200',
      facilityAddress: '8 Civic Dr, Epping VIC 3076',
      ndisProviderNumber: '405 555 666 77',
      status: 'Dispatched',
      deliveryMethod: 'Pharmacy Delivery',
      deliverySchedule: 'Monthly — 1st week',
      pharmacy: 'Blooms The Chemist Melbourne Central',
      createdAt: '2025-07-10T09:00:00',
      updatedAt: '2025-07-14T16:00:00',
      notes: 'Monthly delivery across 3 SIL houses. Delivery split: 4 Civic Dr (Liam, Priya, Sam), 18 Cooper St (Jade, Tyler), 9 Creek Rd (Ben). Controlled meds — James must countersign.',
      participants: [
        {
          name: 'Priya Sharma',
          ndisNumber: '431 666 777',
          planManager: 'First Choice Plan Managers',
          supportCategory: 'Core — Consumables',
          items: [
            { name: 'Lithium 250mg', quantity: 60, price: 6.80, type: 'medication' },
            { name: 'Quetiapine 100mg', quantity: 30, price: 6.80, type: 'medication' },
            { name: 'Levothyroxine 50mcg', quantity: 30, price: 6.80, type: 'medication' },
            { name: 'Blood Glucose Test Strips (50)', quantity: 2, price: 16.90, type: 'consumable' }
          ]
        },
        {
          name: 'Sam Trevorrow',
          ndisNumber: '431 777 888',
          planManager: 'First Choice Plan Managers',
          supportCategory: 'Core — Consumables',
          items: [
            { name: 'Clozapine 100mg', quantity: 30, price: 6.80, type: 'medication' },
            { name: 'Metformin 500mg', quantity: 60, price: 6.80, type: 'medication' },
            { name: 'Pantoprazole 40mg', quantity: 30, price: 6.80, type: 'medication' }
          ]
        },
        {
          name: 'Jade Nguyen',
          ndisNumber: '431 888 999',
          planManager: 'NDIS Self-Managed',
          supportCategory: 'Core — Consumables',
          items: [
            { name: 'Fluoxetine 20mg', quantity: 30, price: 6.80, type: 'medication' },
            { name: 'Buspirone 10mg', quantity: 60, price: 6.80, type: 'medication' },
            { name: 'Oral Supplement Ensure (Pack 6)', quantity: 4, price: 32.00, type: 'consumable' }
          ]
        },
        {
          name: 'Tyler O\'Connor',
          ndisNumber: '431 999 000',
          planManager: 'My Plan Manager',
          supportCategory: 'Core — Consumables',
          items: [
            { name: 'Dexamphetamine 5mg', quantity: 60, price: 42.49, type: 'medication' },
            { name: 'Melatonin 2mg', quantity: 30, price: 6.80, type: 'medication' },
            { name: 'Continence Pads (Pack 20)', quantity: 8, price: 28.50, type: 'consumable' }
          ]
        },
        {
          name: 'Ben Hartley',
          ndisNumber: '431 000 111',
          planManager: 'First Choice Plan Managers',
          supportCategory: 'Core — Consumables',
          items: [
            { name: 'Carbamazepine 200mg', quantity: 60, price: 6.80, type: 'medication' },
            { name: 'Clobazam 10mg', quantity: 30, price: 6.80, type: 'medication' },
            { name: 'Vitamin D 1000IU', quantity: 30, price: 0, type: 'medication' },
            { name: 'Wound Care Kit', quantity: 1, price: 18.90, type: 'consumable' }
          ]
        },
        {
          name: 'Liam Osei',
          ndisNumber: '431 112 223',
          planManager: 'First Choice Plan Managers',
          supportCategory: 'Core — Consumables',
          items: [
            { name: 'Sodium Valproate 500mg', quantity: 60, price: 6.80, type: 'medication' },
            { name: 'Olanzapine 5mg', quantity: 30, price: 6.80, type: 'medication' },
            { name: 'Latex-Free Gloves Box 100', quantity: 4, price: 15.50, type: 'consumable' }
          ]
        }
      ],
      total: 580.79
    },
    // Respite Aged Care — Short-stay
    {
      id: 'FORD009',
      locationId: 'LOC001',
      type: 'facility',
      facilityType: 'Aged Care',
      facilityName: 'Kew Respite & Transitional Care',
      facilityContact: 'Nurse Jenny Liu — Respite Coordinator',
      facilityPhone: '+61 3 9853 4400',
      facilityAddress: '30 Cotham Rd, Kew VIC 3101',
      status: 'Ready for Dispatch',
      deliveryMethod: 'Pharmacy Delivery',
      deliverySchedule: 'As needed — Respite admissions',
      pharmacy: 'Blooms The Chemist Melbourne Central',
      createdAt: '2025-07-14T16:30:00',
      updatedAt: '2025-07-15T11:00:00',
      notes: 'Respite admissions — short-stay patients (1-4 weeks). Urgent turnaround needed. All packs labelled with admission & discharge dates. Contact Jenny before delivery.',
      patients: [
        {
          name: 'Shirley McDonald',
          room: 'Resp-01',
          packType: 'Webster-pak (7 days)',
          isNew: true,
          items: [
            { name: 'Metoprolol 50mg', quantity: 14, price: 6.80 },
            { name: 'Irbesartan 150mg', quantity: 7, price: 6.80 },
            { name: 'Atorvastatin 20mg', quantity: 7, price: 6.80 },
            { name: 'Metformin 500mg', quantity: 14, price: 6.80 },
            { name: 'Aspirin 100mg', quantity: 7, price: 0 }
          ]
        },
        {
          name: 'Colin Fraser',
          room: 'Resp-03',
          packType: 'Webster-pak (14 days)',
          isNew: true,
          items: [
            { name: 'Ramipril 5mg', quantity: 14, price: 6.80 },
            { name: 'Amlodipine 10mg', quantity: 14, price: 6.80 },
            { name: 'Rosuvastatin 20mg', quantity: 14, price: 6.80 },
            { name: 'Pantoprazole 40mg', quantity: 14, price: 6.80 },
            { name: 'Paracetamol 500mg', quantity: 28, price: 0 },
            { name: 'Vitamin D 1000IU', quantity: 14, price: 0 }
          ]
        },
        {
          name: 'Moira Singh',
          room: 'Resp-04',
          packType: 'Webster-pak (7 days)',
          isNew: true,
          items: [
            { name: 'Perindopril 5mg', quantity: 7, price: 6.80 },
            { name: 'Gliclazide MR 60mg', quantity: 7, price: 6.80 },
            { name: 'Metformin 1000mg', quantity: 14, price: 6.80 },
            { name: 'Rosuvastatin 10mg', quantity: 7, price: 6.80 }
          ]
        },
        {
          name: 'Raymond Tan',
          room: 'Resp-06',
          packType: 'Webster-pak (7 days)',
          isNew: true,
          items: [
            { name: 'Warfarin 2mg', quantity: 7, price: 6.80 },
            { name: 'Digoxin 125mcg', quantity: 7, price: 6.80 },
            { name: 'Frusemide 40mg', quantity: 7, price: 6.80 },
            { name: 'Potassium Chloride 600mg', quantity: 7, price: 6.80 },
            { name: 'Omeprazole 20mg', quantity: 7, price: 6.80 }
          ]
        }
      ],
      total: 163.20
    },
    // NDIS — Psychosocial Disability Support
    {
      id: 'FORD010',
      locationId: 'LOC001',
      type: 'facility',
      facilityType: 'NDIS',
      facilityName: 'Inner East Psychosocial Recovery Hub',
      facilityContact: 'Dr. Mei Lin — Clinical Lead / Tanya Brooks — Recovery Coach Lead',
      facilityPhone: '+61 3 9882 1100',
      facilityAddress: '42 Glenferrie Rd, Hawthorn VIC 3122',
      ndisProviderNumber: '405 888 999 00',
      status: 'Packing',
      deliveryMethod: 'Pharmacy Delivery',
      deliverySchedule: 'Weekly — Friday AM',
      pharmacy: 'Blooms The Chemist Melbourne Central',
      createdAt: '2025-07-14T13:00:00',
      updatedAt: '2025-07-15T09:45:00',
      notes: 'Psychosocial recovery service. Participants attend day program MWF. Webster-paks left with reception. Clozapine patients require current blood results before dispensing — check pathology.',
      participants: [
        {
          name: 'Daniel Yusuf',
          ndisNumber: '431 700 801',
          planManager: 'National Plan Managers',
          supportCategory: 'Core — Consumables',
          items: [
            { name: 'Clozapine 200mg', quantity: 7, price: 6.80, type: 'medication' },
            { name: 'Metformin 500mg', quantity: 14, price: 6.80, type: 'medication' },
            { name: 'Pantoprazole 20mg', quantity: 7, price: 6.80, type: 'medication' },
            { name: 'Blood Glucose Test Strips (50)', quantity: 1, price: 16.90, type: 'consumable' }
          ]
        },
        {
          name: 'Ashleigh Morgan',
          ndisNumber: '431 801 902',
          planManager: 'National Plan Managers',
          supportCategory: 'Core — Consumables',
          items: [
            { name: 'Lithium 450mg', quantity: 7, price: 6.80, type: 'medication' },
            { name: 'Quetiapine 200mg', quantity: 7, price: 6.80, type: 'medication' },
            { name: 'Levothyroxine 75mcg', quantity: 7, price: 6.80, type: 'medication' }
          ]
        },
        {
          name: 'Ryan Patel',
          ndisNumber: '431 902 003',
          planManager: 'My Plan Manager',
          supportCategory: 'Core — Consumables',
          items: [
            { name: 'Paliperidone ER 6mg', quantity: 7, price: 6.80, type: 'medication' },
            { name: 'Escitalopram 20mg', quantity: 7, price: 6.80, type: 'medication' },
            { name: 'Diazepam 5mg PRN', quantity: 7, price: 6.80, type: 'medication' }
          ]
        },
        {
          name: 'Melissa Cartwright',
          ndisNumber: '431 003 104',
          planManager: 'National Plan Managers',
          supportCategory: 'Core — Consumables',
          isNew: true,
          items: [
            { name: 'Olanzapine 15mg', quantity: 7, price: 6.80, type: 'medication' },
            { name: 'Sodium Valproate 500mg', quantity: 14, price: 6.80, type: 'medication' },
            { name: 'Metformin 500mg', quantity: 14, price: 6.80, type: 'medication' },
            { name: 'Nicotine Patches 21mg (7)', quantity: 1, price: 28.00, type: 'consumable' }
          ]
        },
        {
          name: 'Alex Tham',
          ndisNumber: '431 104 205',
          planManager: 'NDIS Self-Managed',
          supportCategory: 'Core — Consumables',
          items: [
            { name: 'Aripiprazole 15mg', quantity: 7, price: 6.80, type: 'medication' },
            { name: 'Venlafaxine XR 150mg', quantity: 7, price: 6.80, type: 'medication' },
            { name: 'Zopiclone 7.5mg PRN', quantity: 7, price: 6.80, type: 'medication' }
          ]
        }
      ],
      total: 196.50
    }
  ]),
  inventory: savedState?.inventory || buildInventory(),
  notifications: [],
  filters: {
    categories: [],
    priceRange: [0, 100],
    prescription: null,
    pbs: null,
    inStock: null,
    sort: 'name'
  }
};

export function appReducer(state, action) {
  switch (action.type) {
    case 'SET_ACTIVE_TAB':
      return { ...state, activeTab: action.payload };

    case 'SET_SEARCH_QUERY':
      return { ...state, searchQuery: action.payload };

    case 'SELECT_CUSTOMER':
      return { ...state, selectedCustomerId: action.payload, customerSubTab: 'profile' };

    case 'CLEAR_SELECTED_CUSTOMER':
      return { ...state, selectedCustomerId: null, customerSubTab: 'profile' };

    case 'SET_CUSTOMER_SUB_TAB':
      return { ...state, customerSubTab: action.payload };

    case 'OPEN_EMERGENCY_SUPPLY':
      return { ...state, emergencySupply: action.payload };

    case 'CLEAR_EMERGENCY_SUPPLY':
      return { ...state, emergencySupply: null };

    case 'UPDATE_CUSTOMER': {
      return {
        ...state,
        customers: state.customers.map(c =>
          c.id === action.payload.id ? { ...c, ...action.payload } : c
        )
      };
    }

    case 'ADD_PRESCRIPTION_TO_CUSTOMER': {
      const { customerId, prescription } = action.payload;
      return {
        ...state,
        customers: state.customers.map(c =>
          c.id === customerId
            ? { ...c, prescriptions: [...c.prescriptions, prescription] }
            : c
        )
      };
    }

    case 'UPDATE_PRESCRIPTION_STATUS': {
      const { customerId, prescriptionId, status } = action.payload;
      return {
        ...state,
        customers: state.customers.map(c =>
          c.id === customerId
            ? {
                ...c,
                prescriptions: c.prescriptions.map(rx =>
                  rx.id === prescriptionId ? { ...rx, status } : rx
                )
              }
            : c
        )
      };
    }

    case 'ADD_TO_DISPENSING_QUEUE':
      return {
        ...state,
        dispensingQueue: [...state.dispensingQueue, action.payload]
      };

    case 'SELECT_DISPENSING_ITEM':
      return { ...state, selectedDispensingId: action.payload };

    case 'UPDATE_DISPENSING_ITEM':
      return {
        ...state,
        dispensingQueue: state.dispensingQueue.map(item =>
          item.id === action.payload.id ? { ...item, ...action.payload } : item
        )
      };

    case 'COMPLETE_DISPENSING': {
      const item = state.dispensingQueue.find(i => i.id === action.payload.id);
      if (!item) return state;

      const historyEntry = {
        medication: item.medication,
        date: new Date().toISOString().split('T')[0],
        quantity: item.quantity,
        pbsCost: action.payload.pbsCost || 6.80,
        dispensedBy: state.staff.name
      };

      return {
        ...state,
        dispensingQueue: state.dispensingQueue.filter(i => i.id !== action.payload.id),
        selectedDispensingId: null,
        customers: state.customers.map(c =>
          c.id === item.customerId
            ? {
                ...c,
                dispensingHistory: [historyEntry, ...c.dispensingHistory],
                prescriptions: c.prescriptions.map(rx =>
                  rx.id === item.prescriptionId && rx.refillsRemaining > 0
                    ? { ...rx, refillsRemaining: rx.refillsRemaining - 1, lastFilledDate: new Date().toISOString().split('T')[0] }
                    : rx
                )
              }
            : c
        ),
        inventory: state.inventory.map(med =>
          med.name === item.medication
            ? { ...med, stockLevel: Math.max(0, med.stockLevel - item.quantity), inStock: med.stockLevel - item.quantity > 0 }
            : med
        )
      };
    }

    case 'EMERGENCY_DISPENSE': {
      const { customerId, medication, quantity, reason, dispensedBy, date } = action.payload;
      const historyEntry = {
        medication,
        date: date.split('T')[0],
        quantity,
        pbsCost: null,
        dispensedBy,
        isEmergencySupply: true,
        emergencyReason: reason
      };

      return {
        ...state,
        customers: state.customers.map(c =>
          c.id === customerId
            ? { ...c, dispensingHistory: [historyEntry, ...c.dispensingHistory] }
            : c
        ),
        inventory: state.inventory.map(med =>
          med.name === medication
            ? { ...med, stockLevel: Math.max(0, med.stockLevel - quantity), inStock: med.stockLevel - quantity > 0 }
            : med
        )
      };
    }

    case 'REMOVE_FROM_DISPENSING_QUEUE':
      return {
        ...state,
        dispensingQueue: state.dispensingQueue.filter(i => i.id !== action.payload),
        selectedDispensingId: state.selectedDispensingId === action.payload ? null : state.selectedDispensingId
      };

    case 'UPDATE_INVENTORY_STOCK':
      return {
        ...state,
        inventory: state.inventory.map(med =>
          med.id === action.payload.id
            ? { ...med, stockLevel: action.payload.stockLevel, inStock: action.payload.stockLevel > 0 }
            : med
        )
      };

    case 'ADD_ORDER':
      return { ...state, orders: [action.payload, ...state.orders] };

    case 'UPDATE_ORDER_STATUS':
      return {
        ...state,
        orders: state.orders.map(order =>
          order.id === action.payload.id
            ? { ...order, status: action.payload.status, updatedAt: new Date().toISOString() }
            : order
        )
      };

    case 'ADD_NOTIFICATION':
      return { ...state, notifications: [action.payload, ...state.notifications] };

    case 'MARK_NOTIFICATION_READ':
      return {
        ...state,
        notifications: state.notifications.map(n =>
          n.id === action.payload ? { ...n, read: true } : n
        )
      };

    case 'MARK_ALL_NOTIFICATIONS_READ':
      return {
        ...state,
        notifications: state.notifications.map(n => ({ ...n, read: true }))
      };

    case 'UPDATE_FILTERS':
      return { ...state, filters: { ...state.filters, ...action.payload } };

    case 'RESET_FILTERS':
      return {
        ...state,
        filters: { categories: [], priceRange: [0, 100], prescription: null, pbs: null, inStock: null, sort: 'name' }
      };

    case 'SET_ACTIVE_LOCATION': {
      const loc = locations.find(l => l.id === action.payload);
      return {
        ...state,
        activeLocationId: action.payload,
        staff: {
          ...state.staff,
          pharmacyName: loc?.fullName || state.staff.pharmacyName
        }
      };
    }

    case 'REQUEST_STOCK_TRANSFER': {
      const transfer = {
        id: `TRF${String(state.stockTransfers.length + 1).padStart(3, '0')}`,
        ...action.payload,
        status: 'Pending',
        requestedAt: new Date().toISOString(),
        completedAt: null
      };
      return {
        ...state,
        stockTransfers: [transfer, ...state.stockTransfers]
      };
    }

    case 'APPROVE_STOCK_TRANSFER':
      return {
        ...state,
        stockTransfers: state.stockTransfers.map(t =>
          t.id === action.payload.id
            ? { ...t, status: 'In Transit', approvedBy: action.payload.approvedBy }
            : t
        )
      };

    case 'COMPLETE_STOCK_TRANSFER': {
      const transfer = state.stockTransfers.find(t => t.id === action.payload);
      if (!transfer) return state;

      return {
        ...state,
        stockTransfers: state.stockTransfers.map(t =>
          t.id === action.payload
            ? { ...t, status: 'Completed', completedAt: new Date().toISOString() }
            : t
        ),
        inventory: state.inventory.map(med => {
          if (med.name !== transfer.medication) return med;
          const fromStock = (med.stockByLocation?.[transfer.fromLocation] || 0);
          const toStock = (med.stockByLocation?.[transfer.toLocation] || 0);
          return {
            ...med,
            stockByLocation: {
              ...med.stockByLocation,
              [transfer.fromLocation]: Math.max(0, fromStock - transfer.quantity),
              [transfer.toLocation]: toStock + transfer.quantity
            }
          };
        })
      };
    }

    case 'REJECT_STOCK_TRANSFER':
      return {
        ...state,
        stockTransfers: state.stockTransfers.map(t =>
          t.id === action.payload
            ? { ...t, status: 'Rejected' }
            : t
        )
      };

    case 'CLEAR_SAVED_DATA':
      localStorage.removeItem('medfast-pharmacy-state');
      return {
        ...state,
        dispensingQueue: defaultQueue,
        orders: initialState.orders,
        inventory: buildInventory(),
        stockTransfers: defaultTransfers,
        notifications: []
      };

    default:
      return state;
  }
}

export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Persist relevant state
  useEffect(() => {
    const stateToPersist = {
      _dataVersion: DATA_VERSION,
      dispensingQueue: state.dispensingQueue,
      orders: state.orders,
      inventory: state.inventory,
      stockTransfers: state.stockTransfers,
      activeLocationId: state.activeLocationId
    };
    localStorage.setItem('medfast-pharmacy-state', JSON.stringify(stateToPersist));
  }, [state.dispensingQueue, state.orders, state.inventory, state.stockTransfers, state.activeLocationId]);

  // Initialize pharmacy notifications
  useEffect(() => {
    const initialNotifications = [
      {
        id: 1,
        type: 'erx',
        title: 'New eRx Script Received',
        message: 'Metformin 500mg for Sarah Johnson (ERXT-2025-001-MET) — ready for dispensing',
        time: '2 min ago',
        read: false,
        action: 'Dispense'
      },
      {
        id: 2,
        type: 'stock',
        title: 'Low Stock Alert',
        message: 'Lisinopril 10mg stock level is 12 units — below reorder threshold',
        time: '15 min ago',
        read: false,
        action: 'View Inventory'
      },
      {
        id: 3,
        type: 'pbs',
        title: 'PBS Safety Net Alert',
        message: "Michael O'Brien is $44 away from PBS Safety Net threshold ($1,650 / $1,694)",
        time: '1 hour ago',
        read: false,
        action: 'View Customer'
      },
      {
        id: 4,
        type: 'expiry',
        title: 'Prescription Expiring',
        message: "Sarah Johnson's Lisinopril 10mg (RX002) expires on 2025-07-26",
        time: '2 hours ago',
        read: true,
        action: 'View Script'
      },
      {
        id: 5,
        type: 'order',
        title: 'Order Ready for Pickup',
        message: 'Order #ORD001 for Sarah Johnson is ready for collection',
        time: '3 hours ago',
        read: true,
        action: 'View Order'
      }
    ];

    initialNotifications.forEach(notification => {
      dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
    });
  }, []);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};
